<template>
  <section :class="$style.board" :aria-label="t('aria')">
    <header :class="$style.header">
      <div :class="$style.headerRow">
        <div :class="$style.headerMain">
          <ui-button
            v-if="isTablet"
            :class="$style.headerBackButton"
            rounded
            fill
            size="l"
            icon-left="chevron-left"
            :aria-label="t('back')"
            @click="emit('close')"
          />
          <ui-button
            v-else
            theme="default"
            size="l"
            icon-left="chevron-left"
            @click="emit('close')"
          >
            {{ t('back') }}
          </ui-button>

          <div :class="$style.headerMeta">
            <h1 :class="$style.title">{{ t('title') }}</h1>
            <ul :class="$style.metaList">
              <li
                v-if="tournament.game.name"
                :class="[$style.metaItem, $style.metaItemGame]"
              >
                <ui-icon name="cs2-filled" :class="$style.metaIcon" />
                {{ tournament.game.name }}
              </li>
              <li :class="$style.metaItem">{{ tournament.name }}</li>
              <li v-if="activeStagePosition !== null" :class="$style.metaItem">
                {{ activeStagePosition }} {{ t('stage') }}
                <span :class="$style.metaAccent">
                  {{ t('stageOf', { total: stages.length }) }}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div :class="$style.searchWrapper">
          <ui-icon name="search" :class="$style.searchIcon" />
          <input
            :value="store.searchInput"
            type="text"
            :placeholder="t('searchPlaceholder')"
            :class="$style.search"
            :aria-label="t('searchAria')"
            @input="onSearchInput"
          />
          <button
            v-if="store.searchInput"
            type="button"
            :class="$style.clearBtn"
            :aria-label="t('clear')"
            @click="onClear"
          >
            <ui-icon name="x" :class="$style.clearIcon" />
          </button>
        </div>
      </div>

      <p v-if="hintShown" :class="$style.hint">
        {{ t('searchHint', { min: LEADERBOARD_SEARCH_MIN_LENGTH }) }}
      </p>
    </header>

    <div :class="$style.body">
      <ui-spinner
        v-if="store.isLoadingPage && !store.items.length"
        :class="$style.spinner"
        theme="accent"
      />

      <div
        v-else
        :class="[$style.content, { [$style.contentEmpty]: isContentEmpty }]"
      >
        <div v-if="showPodium" :class="$style.podium">
          <leader-board-podium-card
            v-for="row in podiumItems"
            :key="`podium-${row.position}-${row.member.id}`"
            :place="row.position"
            :member="row.member"
            :xp="row.xp"
            :correct-count="row.correct_count"
            :picks-total="picksTotal"
          />
        </div>

        <div :class="$style.list" :aria-busy="store.isLoadingPage">
          <leader-board-list-row
            v-for="row in listItems"
            :key="`${row.position}-${row.member.id}`"
            :position="row.position"
            :member="row.member"
            :xp="row.xp"
            :correct-count="row.correct_count"
            :picks-total="picksTotal"
            :highlight="store.me?.member.id === row.member.id"
          />

          <p v-if="isContentEmpty" :class="$style.empty">
            {{ store.isSearchActive ? t('emptySearch') : t('empty') }}
          </p>
        </div>

        <leaderboard-pagination
          v-if="store.totalPages > 1"
          :page="store.page"
          :total-pages="store.totalPages"
          :disabled="store.isLoadingPage"
          @go="store.goToPage"
        />
      </div>

      <div :class="$style.meBarWrapper" v-if="showMeBar">
        <leader-board-list-row
          :position="store.me!.position"
          :member="store.me!.member"
          :xp="store.me!.xp"
          :correct-count="store.me!.correct_count"
          :picks-total="picksTotal"
          highlight
        />
      </div>
    </div>
  </section>
</template>

<i18n lang="yaml">
ru:
  aria: Лидерборд турнира
  title: Лидерборд
  back: К турниру
  searchPlaceholder: Никнейм игрока
  searchAria: Поиск по никнейму
  searchHint: 'Введите минимум {min} символа для поиска'
  clear: Очистить
  empty: Пока нет результатов
  emptySearch: Никого не нашли по этому запросу
  stage: этап
  stageOf: 'из {total}'
</i18n>

<script lang="ts" setup>
import { computed, onMounted, watch } from 'vue'
import UiButton from '@/ui-core/components/button/index.vue'
import UiIcon from '@/ui-core/components/icon/index.vue'
import UiSpinner from '@/ui-core/components/spinner/index.vue'
import LeaderBoardListRow from './LeaderBoardListRow.vue'
import LeaderBoardPodiumCard from './LeaderBoardPodiumCard.vue'
import LeaderboardPagination from './LeaderboardPagination.vue'
import {
  useLeaderboardStore,
  LEADERBOARD_SEARCH_MIN_LENGTH
} from '../../stores/leaderboard'
import { usePickemAnalytics } from '../../composables/use-pickem-analytics'
import { usePickemLeaderboardRoute } from '../../composables/use-pickem-leaderboard-route'
import type { StageSummary, Tournament } from '../../types'

defineOptions({ name: 'PickemLeaderBoard' })

const props = defineProps<{
  tournament: Tournament
  stages: StageSummary[]
  activeStageId: number | null
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { $matchMedia } = useNuxtApp()
const isTablet = computed(() => $matchMedia.down('md'))
const store = useLeaderboardStore()
const { trackLeaderboardScreenView } = usePickemAnalytics()
const {
  page: routePage,
  search: routeSearch,
  syncState,
  matchesStoreState
} = usePickemLeaderboardRoute()

const loadFromRoute = () => {
  if (
    store.tournamentId === props.tournament.id &&
    matchesStoreState({
      page: store.page,
      searchInput: store.searchInput,
      appliedSearch: store.appliedSearch
    }) &&
    !store.isLoadingPage
  ) {
    return
  }

  store
    .open(props.tournament.id, {
      page: routePage.value,
      search: routeSearch.value
    })
    .catch(() => undefined)
}

onMounted(() => {
  trackLeaderboardScreenView(props.tournament)
  loadFromRoute()
})

watch(
  () => [props.tournament.id, routePage.value, routeSearch.value] as const,
  () => {
    loadFromRoute()
  }
)

watch(
  () => [store.page, store.appliedSearch] as const,
  ([page, search]) => {
    syncState({ page, search })
  }
)

const isContentEmpty = computed(
  () => !store.isLoadingPage && store.items.length === 0
)

const picksTotal = computed(() =>
  props.stages.reduce((sum, stage) => sum + stage.slots_total, 0)
)

const activeStagePosition = computed(() => {
  if (!props.stages.length) return null

  const index = props.stages.findIndex(
    stage => stage.id === props.activeStageId
  )
  const current = index >= 0 ? props.stages[index] : props.stages[0]

  return current.position
})

const showPodium = computed(
  () =>
    !store.isSearchActive && store.page === 1 && podiumItems.value.length > 0
)

const podiumItems = computed(() =>
  store.items.filter(row => row.position >= 1 && row.position <= 3)
)

const listItems = computed(() => {
  if (store.isSearchActive || store.page > 1) return store.items
  return store.items.filter(row => row.position > 3)
})

const showMeBar = computed(
  () => Boolean(store.me) && !store.meVisibleOnPage && !store.isSearchActive
)

const hintShown = computed(() => {
  const len = store.searchInput.trim().length
  return len > 0 && len < LEADERBOARD_SEARCH_MIN_LENGTH
})

const onSearchInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  store.setSearch(target.value)
}

const onClear = () => {
  store.setSearch('')
}
</script>

<style lang="scss" module>
$body-horizontal-mobile-padding: 1.6em;
$header-height: 9.6em;
$header-height-tablet: 15em;
$header-height-mobile: 13.4em;
.board {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  height: 100%;
}

.header {
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  padding: 2.4em;
  background-color: color-get(--root-rgb-bg-card);
  border-radius: 2em 2em 0 0;
  height: $header-height;

  @include down(md) {
    height: $header-height-tablet;
  }

  @include down(sm) {
    gap: 0.4em;
    padding: 1.2em 1.6em 1.6em;
    height: $header-height-mobile;
    border-radius: 0;
    background: color-get(--root-rgb-bg-special-1);
  }
}

.headerRow {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1.2em;

  @include down(sm) {
    flex-direction: column;
    gap: 1.6em;
  }
}

.headerMain {
  display: flex;
  flex: 1 1 16em;
  align-items: flex-start;
  gap: 1.2em;
  min-width: 0;

  @include down(md) {
    flex: 1 1 auto;
    width: 100%;
  }
}

.headerBackButton {
  --button-fill-color: #{color-get(--root-rgb-text-2)};
}

.headerMeta {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 0.4em;
  min-width: 0;
}

.title {
  margin: 0;
  @include typo-get(subhead-l);
  color: color-get(--root-rgb-text-1);
}

.metaList {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1em;
  margin: 0;
  padding: 0;
  list-style: none;
}

.metaItem {
  display: inline-flex;
  align-items: center;
  gap: 0.2em;
  @include typo-get(body-s);
  color: color-get(--root-rgb-text-2);

  & + & {
    &::before {
      content: '•';
      margin-right: 0.2em;
      color: color-get(--root-rgb-text-2);

      @include down(sm) {
        display: none;
      }
    }
  }
}

.metaItemGame {
  @include down(sm) {
    display: none;
  }
}

.metaIcon {
  width: 1.4em;
  height: 1.4em;
}

.metaAccent {
  color: color-get(--root-rgb-text-1);
}

.searchWrapper {
  position: relative;
  display: flex;
  flex: 0 1 25em;
  align-items: center;
  min-width: 0;

  @include down(md) {
    flex: 1 1 auto;
    width: 100%;
  }
}

.searchIcon {
  position: absolute;
  left: 1.2em;
  width: 1.4em;
  height: 1.4em;
  color: color-get(--root-rgb-text-2);
  pointer-events: none;
}

.search {
  width: 100%;
  appearance: none;
  background-color: color-get(--root-rgb-control-default-bg);
  border: 1px solid transparent;
  border-radius: 0.4em;
  padding: 0.7em 0.7em 0.7em 2.2em;
  color: color-get(--root-rgb-text-1);
  @include typo-get(body-m);
  outline: none;

  &:focus {
    border-color: color-get(--root-rgb-accent-2);
  }

  &::placeholder {
    color: color-get(--root-rgb-text-2);
  }
}

.clearBtn {
  position: absolute;
  right: 0.4em;
  top: 50%;
  transform: translateY(-50%);
  appearance: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.4em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: color-get(--root-rgb-text-3);

  @include hover-focus {
    color: color-get(--root-rgb-text-1);
  }
}

.clearIcon {
  width: 1.4em;
  height: 1.4em;
}

.hint {
  margin: 0;
  @include typo-get(body-xs);
  color: color-get(--root-rgb-text-3);

  @include down(sm) {
    display: none;
  }
}

.body {
  position: relative;
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: 1.6em;
  padding: 2.4em;
  background-color: color-get(--root-rgb-bg-tone-dark);
  border-radius: 0 0 1.2em 1.2em;
  min-height: calc(100% - $header-height);

  @include down(md) {
    min-height: calc(100% - $header-height-tablet);
  }

  @include down(sm) {
    gap: 0.8em;
    min-height: calc(100% - $header-height-mobile);
    padding: 0 $body-horizontal-mobile-padding 0;
    background: color-get(--root-rgb-bg-special-1);
  }
}

.content {
  display: flex;
  flex-direction: column;
  gap: 1.6em;
  height: 100%;

  @include down(sm) {
    padding-bottom: 2em;
  }
}

.contentEmpty {
  justify-content: center;
  border: 1px solid color-get(--root-rgb-text-3);
  border-radius: 2.4em;
}

.spinner {
  margin: 4em auto;
}

.podium {
  display: flex;
  gap: 0.8em;

  @include down(sm) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.2em;

    > :first-child {
      grid-column: 1 / -1;
    }
  }
}

.list {
  display: flex;
  flex-direction: column;
  gap: 0.4em;
  padding-bottom: 0.4em;
}

.empty {
  margin: 0;
  padding: 2.4em 0;
  text-align: center;
  @include typo-get(body-l);
  color: color-get(--root-rgb-text-2);
}

.meBarWrapper {
  position: sticky;
  bottom: 1.6em;
  z-index: 1;
  margin-top: auto;

  @include down(sm) {
    bottom: 0;
    margin: 0 (-$body-horizontal-mobile-padding);
    padding: 0.8em $body-horizontal-mobile-padding 2.4em;
    background: rgba(var(--root-rgb-bg-special-1), 1);
  }
}
</style>
